import React, {KeyboardEvent, RefObject, type ReactElement} from "react";
import ReactPlayer, {Config} from "react-player";
import {getSrc} from "gatsby-plugin-image";
import {v4 as uuid} from "uuid";

import Modal from "../../modal/Modal";
import Icon, {IconSVG} from "../../icon/Icon";
import ErrorBoundary from "../../error-boundary/ErrorBoundary";
import {ImageObject} from "../../image/Image";
import TransitionBlock, {
  TRANSITION_BLOCK_FROM,
} from "../../animation/transition-block/TransitionBlock";
import MarkdownText from "../../markdown/markdown-text/MarkdownText";

import useHasWindow from "/src/hooks/useHasWindow";

import DefaultPlayerIcon from "/src/svg/video-player-icon.svg";

import translate from "/src/utils/translate";
import {css, deepMergeObject} from "/src/utils/utils";
import KEY from "/src/constants/keyboard-keydown-key";

import * as styles from "./video.module.scss";

export enum VideoType {
  popup = "popup",
  inline = "inline",
  autoplay = "autoplay",
  clicktoplay = "clicktoplay",
}

export interface VideoProps {
  // make sure to create the svg icon under icon folder
  // import the svg icon, and pass along the value
  customPlayIcon?: IconSVG;
  iconClasses?: string;
  showControl?: boolean;
  type?: VideoType;
  videoClasses?: string;
  videoConfig?: Config;
  videoSource: string;
  videoThumbnail?: ImageObject;
  videoTitle?: string;
  videoRef?: RefObject<ReactPlayer>;
  caption?: string;
  captionClasses?: string;
  playing?: boolean;
  portrait?: boolean;
}

const Video = (props: VideoProps): ReactElement | null => {
  const {
    caption,
    captionClasses,
    customPlayIcon,
    iconClasses,
    showControl = true,
    type = VideoType.autoplay,
    videoClasses,
    videoConfig = {},
    videoSource,
    videoThumbnail,
    videoTitle = translate("Video Player"),
    videoRef,
    playing,
    portrait = false,
  } = props;

  let playingState = playing === undefined && type === "autoplay";

  if (playing != null) {
    playingState = playing;
  }

  const baseElemId = uuid();

  const PlayButton = (
    <Icon
      role="presentation"
      aria-label={translate("Play Video")}
      classes={css(styles.video__icon, iconClasses!)}
      svg={customPlayIcon || DefaultPlayerIcon}
    />
  );

  const playerRef = React.useRef<ReactPlayer | null>(null);
  const playerRefWrapper = React.useRef<HTMLButtonElement | null>(null);

  const [showModal, setShowModal] = React.useState<boolean>(false);

  const [popupCounter, setPopupCounter] = React.useState<number>(0);

  // this is to fix rehydration issue on static build
  // ref: https://github.com/cookpete/react-player/issues/1474
  const hasWindow = useHasWindow();

  const thumbnailSource =
    videoThumbnail && videoThumbnail.gatsbyImageData
      ? getSrc(videoThumbnail.gatsbyImageData)
      : null;

  React.useEffect(() => {
    if (showModal) {
      requestAnimationFrame(() => {
        // using counter as video key id will fix re-rendering error
        // re: unable to find the old player
        // we should use this instead of short id/uuid
        // ref: https://github.com/CookPete/react-player/issues/209#issuecomment-315006770
        setPopupCounter(popupCounter + 1);
      });
    }

    // adding popupCounter as dep, will update the value pre-maturely
    // while the video is still trying to load and play back
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showModal]);

  const playKeyDownHandler = React.useCallback((e: KeyboardEvent) => {
    if (e.key === KEY.ENTER || e.key === KEY.SPACE) {
      e.preventDefault();
      setShowModal(true);
    }
  }, []);

  if (videoSource == null) {
    return null;
  }

  if (!hasWindow) {
    return null;
  }

  const captionContent = caption ? (
    <div className={css(styles.video__caption, captionClasses!)}>
      <TransitionBlock
        triggerOnce
        from={TRANSITION_BLOCK_FROM.bottom}
        delay={150}
        transition="cubic-bezier(0.22, 0.61, 0.36, 1)"
      >
        <MarkdownText text={caption} />
      </TransitionBlock>
    </div>
  ) : null;

  const constructVideo = (type: VideoType) => {
    switch (type) {
      case "popup": {
        const playerConfig = deepMergeObject(
          {
            wistia: {
              options: {
                transparentLetterbox: true,
                playBarControl: showControl,
                playPauseControl: showControl,
                settingsControl: showControl,
                qualityControl: showControl,
                playbackRateControl: showControl,
                volumeControl: showControl,
                fullscreenControl: showControl,
                volume: 0.5,
                aspect: portrait ? "9 / 16" : "16 / 9",
              },
            },
          },
          videoConfig
        );
        return (
          <>
            <button
              aria-label={translate("Play Video")}
              className={css(styles.video__button)}
              onKeyDown={playKeyDownHandler}
              ref={playerRefWrapper}
              type="button"
            >
              <ReactPlayer
                playing={playingState}
                key={`${baseElemId}-${popupCounter}`}
                ref={playerRef}
                className={css(styles.video__thumbnail, videoClasses!)}
                style={{objectFit: "cover"}}
                url={videoSource}
                playIcon={PlayButton}
                height={"100%"}
                width={"100%"}
                config={playerConfig}
                onClickPreview={() => setShowModal(true)}
                light={thumbnailSource || true}
              />
            </button>
            {showModal && (
              <Modal
                portrait={portrait}
                ariaLabelledBy={`${baseElemId}-title`}
                onClose={() => {
                  setShowModal(false);

                  // focus back on the video player on modal close
                  requestAnimationFrame(() => {
                    if (playerRefWrapper.current) {
                      playerRefWrapper.current.focus();
                    }
                  });
                }}
              >
                <div
                  className={css(
                    styles.video,
                    portrait ? styles.videoPortrait : ""
                  )}
                >
                  <h3 id={`${baseElemId}-title`} hidden>
                    {videoTitle}
                  </h3>
                  <ReactPlayer
                    ref={videoRef}
                    key={`${baseElemId}-${popupCounter}`}
                    url={videoSource}
                    className={css(
                      styles.video__player,
                      portrait ? styles.video__playerPortrait : "",
                      videoClasses!
                    )}
                    config={playerConfig}
                    controls={showControl}
                    playing={showModal}
                    height="100%"
                    width="100%"
                  />
                </div>
              </Modal>
            )}
          </>
        );
      }
      case "inline": {
        const playerConfig = deepMergeObject(
          {
            wistia: {
              options: {
                wmode: "transparent",
                played: 0,
              },
            },
          },
          videoConfig
        );
        return (
          <ReactPlayer
            ref={videoRef}
            className={css(
              styles.video__player,
              styles.video__playerInline,
              videoClasses!
            )}
            url={videoSource}
            controls={showControl}
            width="100%"
            height="100%"
            playing={playingState}
            config={playerConfig}
          />
        );
      }
      default: {
        const playerConfig = deepMergeObject(
          {
            wistia: {
              options: {
                transparentLetterbox: true,
                autoplay: type === VideoType.autoplay ? true : false,
                silentAutoplay: type === VideoType.autoplay ? "allow" : false,
                clickForSound: {
                  showText: true,
                },
                endVideoBehavior: "loop",
                bigPlayButton: true,
                bpbTime: false,
                playBarControl: showControl,
                playPauseControl: showControl,
                settingsControl: showControl,
                qualityControl: showControl,
                playbackRateControl: showControl,
                volumeControl: showControl,
                fullscreenControl: showControl,
                volume: 0.5,
                controlsVisibleOnLoad: false,
                aspect: portrait ? "9 / 16" : "16 / 9",
              },
            },
          },
          videoConfig
        );
        return (
          <ErrorBoundary source="wistia">
            <ReactPlayer
              ref={videoRef || playerRef}
              className={css(styles.video__player, videoClasses!)}
              url={videoSource}
              config={playerConfig}
              width="100%"
              height="100%"
            />
          </ErrorBoundary>
        );
      }
    }
  };

  if (!caption) {
    return constructVideo(type);
  }

  return (
    <div className={styles.video__captionWrapper}>
      <div className={styles.video}>{constructVideo(type)}</div>
      {captionContent}
    </div>
  );
};

export default Video;
